import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { UseQueryOptionsWithoutQueryKey } from '../../../react-query-config'
import { useQuery } from '@tanstack/react-query'
import type { ResponseApiError } from '../../../models/axios-error'
import { mapToWeatherCode, type WeatherCode } from '../../weather/weather-code'

export interface UseApiGetWeekIndicatorsRequest {
  channel: 'INSTORE' | 'OMNI'
  aggregation: 'TEAM' | 'STORE'
  storeId: number
  teamId: number | null
  week: string
}

export interface WeekIndicators {
  weekly_data: Record<number, WeekIndicatorsValues>
}

export interface WeekIndicatorsValues {
  realized_gmv: IndicatorValueOrError
  forecast_gmv: IndicatorValueOrError
  progression: IndicatorValueOrError
  margin_rate: IndicatorValueOrError
  weather_code: WeatherCode
}

export interface IndicatorValueOrError {
  in_error: boolean
  value?: number
}

export interface WeekIndicatorsResponse {
  weekly_data: Record<number, WeekIndicatorsValuesResponse>
}

export interface WeekIndicatorsValuesResponse {
  realized_gmv: IndicatorValueOrError
  forecast_gmv: IndicatorValueOrError
  progression: IndicatorValueOrError
  margin_rate: IndicatorValueOrError
  weather_code: string
}

const mapWeeklyData = (weeklyData: WeekIndicatorsResponse['weekly_data']): WeekIndicators['weekly_data'] => {
  return Object.fromEntries(
    Object.entries(weeklyData).map(([day, values]) => [
      Number(day),
      {
        ...values,
        weather_code: mapToWeatherCode(values.weather_code)
      }
    ])
  )
}

const getWeekIndicators = async (config: AxiosRequestConfig): Promise<WeekIndicators> => {
  const axiosPromise: AxiosResponse<WeekIndicatorsResponse> = await axios(config)
  return {
    weekly_data: mapWeeklyData(axiosPromise.data.weekly_data)
  }
}

export const useApiGetWeekIndicators = (
  args: UseApiGetWeekIndicatorsRequest,
  enabled: boolean,
  options?: UseQueryOptionsWithoutQueryKey<WeekIndicators, ResponseApiError>
) => {
  const { storeId, teamId, channel, aggregation, week } = args
  const teamParam = aggregation === 'TEAM' && teamId !== null ? `&teamId=${teamId}` : ''
  const url = `/v2/week-indicators?aggregation=${aggregation}&channel=${channel}&storeId=${storeId}&week=${week}${teamParam}`
  const config: AxiosRequestConfig = { method: 'get', url }

  return useQuery<WeekIndicators, ResponseApiError>({
    queryKey: ['get-week-indicators', url],
    queryFn: () => getWeekIndicators(config),
    enabled,
    ...options
  })
}
